/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { useTranslation } from "react-i18next";
import { AuroraBackground } from "@/components/AuroraBackground";
import { Reveal } from "@/components/Reveal";
import { StoreBadgeGroup } from "@/components/StoreBadges";
import { AppVideo } from "@/components/AppVideo";
import { BookOpen, Heart, Sparkles } from "lucide-react";

const ChristianPage = () => {
  const { t } = useTranslation("site");
  const tk = (k, fb) => t(`christian.${k}`, fb);
  return (
    <>
      <section className="relative pb-12">
        <AuroraBackground variant="gold" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-8 lg:pt-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="aurora-chip mb-6"><span className="size-1.5 rounded-full bg-[var(--aurora-gold)]" />{tk("kicker")}</p>
              <h1 className="font-serif text-[2.6rem] sm:text-5xl lg:text-[4.2rem] tracking-tight font-light leading-[1.05]">
                {tk("title1")}{" "}<em className="italic aurora-shimmer-text">{tk("title2")}</em>{" "}{tk("title3")}
              </h1>
              <p className="mt-6 text-white/75 text-lg font-light max-w-xl">{tk("subtitle")}</p>
              <div className="mt-8">
                <StoreBadgeGroup startLabel={tk("cta")} />
                <p className="mt-3 text-xs text-white/45 tracking-wide">{t("home.freeBadge")}</p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={120}>
              <div className="phone-frame phone-frame--device max-w-[320px] mx-auto animate-float-slow">
                <AppVideo
                  className="phone-screen"
                  island
                  sources={[
                    { src: "/assets/media/clips/EN_christian-prayer-tour.mp4", type: "video/mp4" },
                  ]}
                  poster="/assets/media/clips/EN_christian-prayer-tour.jpg"
                  label={tk("phoneAlt", "Aurora's faith-forward mode composing a Christian prayer in the app")}
                  soundLabel={tk("soundLabel", "Tap for sound")}
                  rounded="49px"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative py-16">
        <div className="mx-auto max-w-6xl px-5 lg:px-8 grid md:grid-cols-3 gap-5">
          {[
            { icon: <BookOpen size={20} />, title: tk("card1T"), body: tk("card1B"), bg: "var(--aurora-emotional)" },
            { icon: <Sparkles size={20} />, title: tk("card2T"), body: tk("card2B"), bg: "var(--aurora-journeys)" },
            { icon: <Heart size={20} />, title: tk("card3T"), body: tk("card3B"), bg: "var(--aurora-coach)" },
          ].map((c) => (
            <Reveal key={c.title}>
              <div className="aurora-card p-7 h-full" style={{ background: c.bg }}>
                <span className="size-11 rounded-2xl bg-white/8 flex items-center justify-center text-[var(--aurora-gold)] mb-5">{c.icon}</span>
                <h3 className="font-serif text-2xl">{c.title}</h3>
                <p className="mt-3 text-white/75 font-light leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="photo-interlude relative py-24 sm:py-32">
        <img
          src="/assets/lifestyle/couple-interlude.jpg"
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="photo-interlude__img"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <div className="photo-interlude__tint" />
        <div className="grain" />
        <div className="relative mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <Reveal>
            <blockquote className="font-serif italic text-2xl sm:text-3xl text-white/90 leading-relaxed">
              "{tk("quote")}"
              <p className="mt-3 not-italic text-sm tracking-wide text-white/50">— {tk("quoteSource")}</p>
            </blockquote>
            <p className="mt-10 text-white/80 font-light">{tk("closingBody")}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <StoreBadgeGroup startLabel={t("common.startFree")} />
            </div>
            <p className="mt-4 text-xs text-white/50 tracking-wide">{t("common.freeFooter")}</p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default ChristianPage;
